import React from "react";
import type { User, Ticket } from "../../generated-axios-client";
import { Card, Flex, Progress, Table, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { countWorkOrdersPerTechnician } from "../dashboard.utils";
import { palette } from "../../theme";

type Props = {
  tickets: Ticket[];
  technicians: User[];
  loading?: boolean;
};

type WorkloadRow = { user: User; open: number; total: number };

function getDisplayName(user: User): string {
  const parts = [user.first_name?.trim(), user.last_name?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : user.username;
}

export const TechnicianWorkloadTable: React.FC<Props> = ({ tickets, technicians, loading }) => {
  const workload = countWorkOrdersPerTechnician(tickets, technicians);
  const maxOpen = Math.max(...workload.map((w) => w.open), 1);

  const columns: TableColumnsType<WorkloadRow> = [
    {
      title: "Technician",
      key: "technician",
      render: (_, row) => (
        <Typography.Text style={{ fontSize: 13 }}>{getDisplayName(row.user)}</Typography.Text>
      ),
    },
    {
      title: "Level",
      key: "level",
      width: 70,
      render: (_, row) =>
        row.user.technician_level ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.user.technician_level}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: "Open WOs",
      key: "open",
      width: 220,
      render: (_, row) => (
        <Flex align="center" gap={8}>
          <Progress
            percent={Math.round((row.open / maxOpen) * 100)}
            showInfo={false}
            strokeColor={palette.accent}
            style={{ flex: 1 }}
            size="small"
          />
          <Typography.Text strong style={{ minWidth: 24, fontSize: 13 }}>
            {row.open}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: "Total",
      key: "total",
      width: 70,
      render: (_, row) => (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {row.total}
        </Typography.Text>
      ),
    },
  ];

  return (
    <Card title="Technician Workload" loading={loading}>
      <Table
        dataSource={workload}
        columns={columns}
        rowKey={(row) => row.user.id}
        size="small"
        pagination={{
          pageSize: 5,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} technicians`,
          size: "small",
          position: ["bottomRight"],
        }}
        locale={{ emptyText: "No technicians found" }}
      />
    </Card>
  );
};
